<template>
  <SimpleModal :data="errorModalData" />

  <div class="form-wrapper">
    <FormKit
      :id="formId"
      type="form"
      :actions="false"
      :errors="errors"
      validation-visibility="submit"
      @submit="doRegister"
    >
      <FormKitMessages />

      <div class="name-container">
        <div class="field--name">
          <FormKit
            type="text"
            name="nameFirst"
            label="First Name"
            validation="required"
          />
        </div>

        <div class="field--name">
          <FormKit
            type="text"
            name="nameLast"
            label="Last Name"
            validation="required"
          />
        </div>

        <template v-if="asSeller">
          <div class="field--country">
            <FormKitStripeConnectCountriesSelect
              type="select"
              name="countryCode"
              label="Country"
              validation="required"
              placeholder="-- Select your country --"
            />
          </div>
        </template>
      </div>

      <p class="desc">
        <template v-if="asSeller">
          Your name and location are required as part of our seller verification process. We currently support
          sellers from Europe and the USA.
        </template>
        <template v-else>
          Your name is required as part of our industry-operator verification process.
        </template>
      </p>

      <FormKit
        type="email"
        name="email"
        label="Email"
        validation="required"
      />

      <FormKit
        type="password"
        name="password"
        label="Password"
        validation="required"
      />

      <template #actions>
        <BaseActions
          :form-id="formId"
          :show-cancel="false"
          :submit-text="asSeller ? 'Continue' : 'Register'"
        />
      </template>
    </FormKit>
  </div>
</template>

<script setup lang="ts">
// Also look to https://programsdream.nl/blog/rest-api-decoupled-drupal-8-user-registration-confirmation
import * as E from 'fp-ts/Either';
import { FormKit, FormKitMessages } from '@formkit/vue';
import { useGtm } from '@gtm-support/vue-gtm';
import { useAuthStore } from '~/store/auth';
// @ts-ignore
import { LoadingState, useLoadingState } from '~/composables/states/loading-state';
import { errorResolver } from '~/composables/error-resolver';
import { useModalData } from '~/composables/modal';
import { useStreamChatStore } from '~/store/stream-chat';
import { DrupalRole } from '~/types/drupal-common';
import { onFbqReady } from '~/common/fb';

const props = withDefaults(defineProps<{
  pad?: boolean
  border?: boolean
  asSeller: boolean,
  loadingState?: LoadingState
}>(), {
  pad: false,
  border: false,
  loadingState: undefined,
});

const authStore = useAuthStore();

const modalData = useModalData();
const streamChatStore = useStreamChatStore();
const gtm = useGtm();
const loadingState = props.loadingState ?? useLoadingState();

const randmail = `test${Math.floor(Math.random() * 1000000)}@handcrafted.market`;

const formId = 'register-form';

const errors = ref<string[]>([]);
const errorModalData = modalData.errorModal;
const emit = defineEmits(['submit', 'finish']);
const doRegister = async (data: {
  email: string,
  password: string,
  firstName: string,
  lastName: string
}) => {
  await loadingState.loadWhile(async () => {
    emit('submit');

    if (!authStore) {
      return;
    }

    const isSeller = props.asSeller;
    const addtlRoles = isSeller
      ? [DrupalRole.SELLER]
      : [];

    const eitherRegisterResponse = await authStore.register({
      ...data,
      roles: addtlRoles,
    });

    if (E.isLeft(eitherRegisterResponse)) {
      const { response } = eitherRegisterResponse.left;
      if (response.status == 422) {
        errors.value = Object.values(response.data.errors)
          .flat();
      } else {
        errors.value = ['We encountered an unexpected error. Please try again later.'];
      }
      return;
    }

    gtm?.push({
      event: 'registrationComplete',
      plan: isSeller
        ? 'seller'
        : 'customer',
    });

    onFbqReady(() => {
      fbq('track', 'CompleteRegistration');
    });

    const authdResponse = await authStore.login(data.email, data.password);
    if (E.isLeft(authdResponse)) {
      // Not sure why we are just console logging this
      // as this state zprobably breaks UX.
      errorResolver.logError(authdResponse.left);
    }

    streamChatStore.connectUser;
    // const destFromRoute = getDestination(route);
    //
    // if (!destFromRoute) {
    //   window.location = isSeller
    //     ? `${apiBaseUrlStore.apiBaseUrl}/d/commerce?tour`
    //     : `${apiBaseUrlStore.apiBaseUrl}/simple-verification?tour`;
    // } else {
    //   const destination = destFromRoute || '/';
    //   router.push(destination);
    // }
    emit('finish');
  });
};

defineExpose({
  loadingState,
});
</script>

<style lang="scss" scoped>

.form-wrapper:has(.formkit-messages) {
  margin-top: -1em !important;

  :deep(.formkit-messages) {
    border: solid red 1px;
    background: var(--color--bg-dark);
    font-weight: 500;
    color: white;
    padding: var(--space-sm);
    padding-left: 2em;
  }
}

.name-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--space-std);

  :deep(.formkit-outer) {
    margin-bottom: 0;
  }

  :deep(.field--country) {
    width: 100%;
  }
}

/* make the country select span both columns */
.field--country {
  grid-column: 1 / -1;
}

.desc {
  font-style: italic;
  font-size: var(--text-sm);
}

:deep(.footer) {
  width: 100%;
}
</style>
